/**
 * AuthApplyCallback · v2.7.65+E69.1
 * ══════════════════════════════════════════════════════════════════════
 * Landing page after Emergent Google Auth redirect for the DRIVER APPLY
 * flow. Purpose: pre-fill the "Apply to Drive" form with the applicant's
 * verified Google identity so they don't have to type email + name.
 *
 * Flow:
 *   1. Parse `#session_id=` from URL fragment.
 *   2. POST it to `/api/auth/google/apply-prefill` (server-side Emergent
 *      exchange, NO DB write).
 *   3. Stash the returned {email, first_name, last_name} in
 *      sessionStorage under `google_apply_prefill` (single-use).
 *   4. Redirect to /login?tab=driver&sub=apply — ApplyPanel reads the
 *      stash on mount and pre-fills its inputs.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AuthApplyCallback() {
  const nav = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const fragment = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(fragment);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error("Missing session id — please try Google sign-in again");
      nav("/login?tab=driver&sub=apply", { replace: true });
      return;
    }
    api.post("/auth/google/apply-prefill", {}, {
      headers: { "X-Session-ID": sessionId },
    })
      .then((r) => {
        const data = r.data || {};
        if (!data.email) {
          toast.error("Google sign-in failed — no email returned");
          nav("/login?tab=driver&sub=apply", { replace: true });
          return;
        }
        try {
          sessionStorage.setItem("google_apply_prefill", JSON.stringify({
            email: data.email,
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            picture: data.picture || null,
            verified_at: new Date().toISOString(),
          }));
        } catch (_e) { /* private browsing */ }
        toast.success(`Verified with Google · ${data.email}`);
        // Full-page nav so LoginPage re-reads sessionStorage on mount.
        window.location.assign("/login?tab=driver&sub=apply");
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Google sign-in failed";
        toast.error(typeof msg === "string" ? msg : (msg?.message || "Google sign-in failed"));
        nav("/login?tab=driver&sub=apply", { replace: true });
      });
  }, [nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100" data-testid="auth-apply-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-400"/>
        <div className="mt-3 text-sm text-zinc-400">Verifying your Google identity…</div>
      </div>
    </div>
  );
}
